import React from 'react';
import c from 'classnames';

import groupBy from '../../../../../shared/helpers/groupBy';

import List from '../../../components/List';
import { CalendarItem } from '../../../types';
import isHTMLElement from '../../../../../shared/helpers/isHtmlElement';

interface Props {
  items: CalendarItem[];
  date: Date;
}

const ViewYearEdgeFader: React.FC = () => {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!ref.current) {
      return () => {};
    }

    const parent = ref.current.parentElement;

    if (!parent) {
      return () => {};
    }

    function translatePosition() {
      if (!parent || !ref.current) {
        return;
      }

      const parentHeight = parent.clientHeight;

      const parentBottom = parent.scrollTop + parentHeight;

      const refHeight = ref.current.clientHeight;

      ref.current.style.transform = `translate3d(0, ${parentBottom - refHeight}px, 0)`;
    }

    parent.addEventListener('scroll', translatePosition);
    window.addEventListener('resize', translatePosition);

    translatePosition();
    return () => {
      parent.removeEventListener('scroll', translatePosition);
      window.removeEventListener('resize', translatePosition);
    };
  }, []);

  return (
    <div className="CalendarWidget__ViewList__EdgeFader" ref={ref} />
  );
};

function findListByDate(date: Date) {
  const headingId = buildMonthHeadingId(date.getFullYear().toString(), (date.getMonth() + 1).toString());

  let list = document.querySelector(`[aria-labelledby="${headingId}"]`);

  // If the list is not found, we try to find the next month. This situation may arise
  // at the end of a month, when there are no more pick up dates for the current month.
  // As a failsafe we try to find the next month for a maximum of 5 times.
  if (!list) {
    const maximumAttemptsToFindList = 5;

    for (let i = 0; i < maximumAttemptsToFindList; i++) {
      const nextMonthHeadingId = buildMonthHeadingId(date.getFullYear().toString(), (date.getMonth() + 1 + i).toString());

      list = document.querySelector(`[aria-labelledby="${nextMonthHeadingId}"]`) as HTMLElement;

      if (list) {
        break;
      }
    }
  }

  return list;
}

const ViewList: React.FC<Props> = ({ items, date }) => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  const itemsSorted = items
    .filter((item) => new Date(item.date) >= currentDate)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const groupedByYear = groupBy(itemsSorted, (item) => String(new Date(item.date).getFullYear()));
  const [shouldEnableFocus, setShouldEnableFocus] = React.useState(false);

  React.useEffect(() => {
    // We disable focus on the first pass since we don't want to focus the list
    // when the user opens the page, but only if a new date is set
    if (!shouldEnableFocus) {
      setShouldEnableFocus(true);
      return () => {};
    }

    const list = findListByDate(date);

    if (!list || !isHTMLElement(list)) {
      return () => {};
    }

    list.setAttribute('tabindex', '-1');

    list.focus();

    function removeTabIndex(ev: FocusEvent) {
      const element = ev.target as HTMLElement;

      element.removeAttribute('tabindex');
    }

    list.addEventListener('blur', removeTabIndex);

    return () => {
      list.removeEventListener('blur', removeTabIndex);
    };
  }, [date]);

  return (
    <div className="CalendarWidget__ViewList">
      <ViewYearEdgeFader />
      {Object.entries(groupedByYear).map(([year, yearItems]) => {
        const keyYear = `year:${year}`;
        const groupedByMonth = groupBy(yearItems, (item) => String(new Date(item.date).toLocaleString('default', { month: 'numeric' })));

        return (
          <div key={keyYear}>
            {Object.entries(groupedByMonth).map(([month, monthItems]) => {
              const newDate = new Date();
              // Make sure to set the date to 1 BEFORE setting the month.
              newDate.setDate(1);
              newDate.setMonth(Number(month) - 1);
              newDate.setFullYear(Number(year));

              const keyMonth = `year:${year}-month:${month}`;
              const classNamesMonth = c('CalendarWidget__ViewYear__month', `CalendarWidget__ViewYear__month--${year}-${month}`);
              const title = newDate.toLocaleString('nl-NL', { month: 'long', year: 'numeric' });

              const headingId = buildMonthHeadingId(year, month);

              return (
                <div key={keyMonth} className={classNamesMonth}>
                  <h3 className="h5 CalendarWidget__ViewYear__month__title" id={headingId}>{title}</h3>
                  <List items={monthItems} ariaLabelledBy={headingId} />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

function buildMonthHeadingId(year: string, month: string) {
  return `CalendarWidget__ViewYear__month__title-${year}-${month}`;
}

export default ViewList;
